import React, { useEffect, useState, useContext, useRef } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faHandshake, faFileCsv, faLock, faEye, faRecycle } from '@fortawesome/free-solid-svg-icons'
import { Formik, Form, Field } from 'formik'
import CustomLoader from '../layouts/CustomLoader'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import jwt_decode from 'jwt-decode'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
  token     : string | null
}
interface DecodedToken { roles: { name: string }[] }
interface Product { id: number; name: string }
interface StatusCount { Status: { name: string }; count: number }
interface PackageUser { email?: string; name?: string; Partner?: { name: string } | null }
interface Pack {
  id           : number
  serial_number: string
  comments     : string
  StatusId     : number
  UsertypeId   : number
  expiry_time  : number
  createdAt    : string
  license_count: number
  Status       : { name: string }
  Time         : { name: string }
  Products     : Product[]
  counts       : StatusCount[]
  User         : PackageUser | null
  Licenses    ?: { product_key: string }[]
}
interface PartnerOption { id: number; name: string }
interface Filters {
  partner  : string
  _package : string
  products : string
  useremail: string
  status   : string
  usertype : string
}

const INIT_FILTERS: Filters = { partner: '0', _package: '', products: '', useremail: '', status: '0', usertype: '' }

const pill = (bg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
  padding: '0.28rem 0.65rem', borderRadius: 9999, backgroundColor: bg,
  color: '#fff', fontWeight: 700, fontSize: '0.75rem', border: 'none',
  cursor: 'pointer', boxShadow: `0 2px 6px ${bg}44`, textDecoration: 'none', whiteSpace: 'nowrap' as const,
})

const statusBadgeClass = (statusId: number) =>
  statusId === 1 ? 'badge text-bg-primary' : statusId === 2 ? 'badge text-bg-danger' : 'badge text-bg-warning'

const buildQuery = (f: Filters) =>
  `&partner=${f.partner}&package=${f._package}&products=${f.products}&useremail=${f.useremail}&status=${f.status}&usertype=${f.usertype}`

const Packages: React.FC = () => {
  const { getHeaders, token } = useContext(AuthContext) as AuthCtx
  const { t }                 = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const decoded  = token ? jwt_decode<DecodedToken>(token) : null
  const role     = decoded?.roles?.[0]?.name ?? ''
  const isAdmin  = role === 'admin'

  const downloadRef    = useRef<HTMLButtonElement>(null)
  const downloadClsRef = useRef<HTMLButtonElement>(null)

  const [data, setData]               = useState<Pack[]>([])
  const [allPartner, setAllPartner]   = useState<PartnerOption[]>([])
  const [partnerFirst, setPartnerFirst] = useState(false)
  const [loading, setLoading]         = useState(false)
  const [totalRows, setTotalRows]     = useState(0)
  const [perPage, setPerPage]         = useState(10)
  const [filters, setFilters]         = useState<Filters>(INIT_FILTERS)
  const [csv, setCsv]                 = useState('10')
  const [searchOpen, setSearchOpen]   = useState(false)

  const fetchPackages = async (page: number, perPageVal = perPage, f = filters) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: Pack[]; total: number; data: PartnerOption[] }>(
        `${url}packages?page=${page}&per_page=${perPageVal}${buildQuery(f)}`, { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
      if (!partnerFirst && res.data.data?.length) {
        setAllPartner(res.data.data)
        setPartnerFirst(true)
      }
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchPackages(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchPackages(page, newPerPage)
  }
  const handleSearch = (values: Filters) => {
    setFilters(values)
    fetchPackages(1, perPage, values)
  }

  const downloadCsv = (blobData: any, filename: string) => {
    const blobUrl = window.URL.createObjectURL(new Blob([blobData]))
    const a       = document.createElement('a')
    a.href        = blobUrl
    a.setAttribute('download', filename)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(blobUrl)
  }

  const getLicensesCsv = async (id: number) => {
    downloadRef.current?.click()
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}licenses/csv/${id}`, { headers })
      downloadCsv(res.data, `licenses-${id}.csv`)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setTimeout(() => downloadClsRef.current?.click(), 500)
    }
  }

  const getPackagesCsv = async () => {
    downloadRef.current?.click()
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}packages/csv/${csv}`, { headers })
      downloadCsv(res.data, `packages-${csv}.csv`)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setTimeout(() => downloadClsRef.current?.click(), 500)
    }
  }

  const changeStatus = async (row: Pack) => {
    const confirm = await Swal.fire({
      title: 'Change Status?', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33', confirmButtonText: 'Change it!',
    })
    if (!confirm.isConfirmed) return

    const { value: newStatus } = await Swal.fire({
      title: 'Change Status',
      html: `<select id="swal-input1" class="form-select mb-3">
        <option value="1" ${row.StatusId === 1 ? 'selected' : ''}>Available</option>
        <option value="2" ${row.StatusId === 2 ? 'selected' : ''}>Disabled</option>
        <option value="3" ${row.StatusId === 3 ? 'selected' : ''}>Activated</option>
        <option value="4" ${row.StatusId === 4 ? 'selected' : ''}>Registered</option>
        <option value="5" ${row.StatusId === 5 ? 'selected' : ''}>Expired</option>
        <option value="6" ${row.StatusId === 6 ? 'selected' : ''}>Renovated</option>
      </select>`,
      focusConfirm: false,
      preConfirm: () => (document.getElementById('swal-input1') as HTMLSelectElement).value,
    })
    if (!newStatus) return

    try {
      const headers = await getHeaders()
      const res     = await axios.put<{ message: Pack[]; total: number; response: string }>(
        `${url}packages/update/${row.id}`, { statusId: newStatus }, { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  useEffect(() => { fetchPackages(1) }, [])

  // ── Columns per role ─────────────────────────────────────────────────────────
  const adminColumns = [
    {
      name: 'Partner',
      cell: (row: Pack) => <span className="small text-capitalize">{(row.User as any)?.Partner?.name ?? '—'}</span>,
    },
    { name: 'Package #S/N',    cell: (row: Pack) => <span className="small">{row.serial_number}</span> },
    { name: 'Comments',        cell: (row: Pack) => <span className="small">{row.comments}</span> },
    { name: 'Compatibility',   cell: (row: Pack) => row.Products.map((p, i) => <p key={i} className="small mb-0 text-capitalize">{p.name}</p>) },
    { name: 'User',            cell: (row: Pack) => <span className="small">{row.User?.email ?? '—'}</span> },
    {
      name: 'Status Package',
      cell: (row: Pack) => (
        <span className={`small ${statusBadgeClass(row.StatusId)}`}>{row.Status.name}</span>
      ),
    },
    {
      name: 'Status Licenses',
      cell: (row: Pack) => row.counts.map((c, i) => (
        <p key={i} className="small mb-0">{c.Status.name}: {c.count}</p>
      )),
    },
    { name: 'Total Licenses', cell: (row: Pack) => <span className="small">{row.license_count}</span> },
    {
      name: 'User type',
      cell: (row: Pack) => {
        const labels: Record<number, string> = { 1: 'Admin', 2: 'Partner', 3: 'Customer (stripe)' }
        return <b className="small">{labels[row.UsertypeId] ?? '—'}</b>
      },
    },
    {
      name: 'Created At',
      cell: (row: Pack) => <span className="small">{row.createdAt.slice(0, 19).replace('T', ' ')}</span>,
    },
    {
      name: 'Expiry time',
      cell: (row: Pack) => <span className="small">{row.expiry_time} {row.Time.name}</span>,
    },
    {
      name    : 'Actions',
      minWidth: '210px',
      cell    : (row: Pack) => (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', padding: '4px 0' }}>
          <Link to={`show/${row.serial_number}`} style={pill(BRAND)}>
            <FontAwesomeIcon icon={faEye} /><span>Show</span>
          </Link>
          <button style={pill('#f59e0b')} onClick={() => changeStatus(row)}>
            <FontAwesomeIcon icon={faRecycle} /><span>Status</span>
          </button>
          <button style={pill('#3b82f6')} onClick={() => getLicensesCsv(row.id)}>
            <FontAwesomeIcon icon={faFileCsv} /><span>CSV</span>
          </button>
        </div>
      ),
    },
  ]

  const partnerColumns = [
    { name: t('packages.package'),      cell: (row: Pack) => <span className="small">{row.serial_number}</span> },
    { name: t('packages.comments'),     cell: (row: Pack) => <span className="small">{row.comments}</span> },
    { name: t('packages.compatibility'), cell: (row: Pack) => row.Products.map((p, i) => <p key={i} className="small mb-0 text-capitalize">{p.name}</p>) },
    { name: t('packages.user_owner'),   cell: (row: Pack) => <span className="small">{row.User?.email ?? '—'}</span> },
    {
      name: t('packages.status_package'),
      cell: (row: Pack) => <span className={`small ${statusBadgeClass(row.StatusId)}`}>{t(`status.${row.Status.name}`)}</span>,
    },
    {
      name: t('packages.status_license'),
      cell: (row: Pack) => row.counts.map((c, i) => (
        <p key={i} className="small mb-0">{t(`status.${c.Status.name}`)}: {c.count}</p>
      )),
    },
    { name: t('packages.total_licenses'), cell: (row: Pack) => <span className="small">{row.license_count}</span> },
    { name: t('packages.created_at'),  cell: (row: Pack) => <span className="small">{row.createdAt.slice(0, 19).replace('T', ' ')}</span> },
    {
      name: t('packages.expire_time'),
      cell: (row: Pack) => {
        const label = row.expiry_time > 1 ? row.Time.name + 's' : row.Time.name
        return <span className="small">{row.expiry_time} {t(`utilities.${label}`)}</span>
      },
    },
    {
      name    : t('packages.actions'),
      minWidth: '150px',
      cell    : (row: Pack) => (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', padding: '4px 0' }}>
          <Link to={`show/${row.serial_number}`} style={pill(BRAND)}>
            <FontAwesomeIcon icon={faEye} /><span>{t('packages.show_all')}</span>
          </Link>
          <button style={pill('#3b82f6')} onClick={() => getLicensesCsv(row.id)}>
            <FontAwesomeIcon icon={faFileCsv} /><span>{t('packages.download_csv')}</span>
          </button>
        </div>
      ),
    },
  ]

  const customerColumns = [
    { name: 'Package #S/N', cell: (row: Pack) => <span className="small">{row.serial_number}</span> },
    {
      name    : 'Licenses',
      minWidth: '270px',
      cell    : (row: Pack) => (
        <div className="my-1">
          {row.Licenses?.map((l, i) => <p key={i} className="small mb-0">{l.product_key}</p>)}
        </div>
      ),
    },
    { name: 'User', cell: (row: Pack) => <span className="small">{row.User?.name ?? '—'}</span> },
    {
      name: 'License counts',
      cell: (row: Pack) => (
        <ul className="ps-0 mb-0">
          {row.counts.map((c, i) => <li key={i} className="small text-capitalize">{c.Status.name}: {c.count}</li>)}
        </ul>
      ),
    },
    {
      name: 'User type',
      cell: (row: Pack) => {
        const labels: Record<number, string> = { 1: 'Created by Admin', 2: 'Acquired by partner', 3: 'Pay online (stripe)' }
        return <span className="small">{labels[row.UsertypeId] ?? '—'}</span>
      },
    },
    {
      name: 'Actions',
      cell: (row: Pack) => (
        <Link to={`show/${row.serial_number}`} style={pill(BRAND)}>
          <FontAwesomeIcon icon={faEye} /><span>Show</span>
        </Link>
      ),
    },
  ]

  const columns = role === 'admin' ? adminColumns : role === 'customer' ? customerColumns : partnerColumns

  return (
    <>
      {/* Download modal trigger/close */}
      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#dlModal" ref={downloadRef} />
      <div className="modal fade" id="dlModal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content" style={{ borderRadius: 16, border: 'none', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <button type="button" className="d-none" data-bs-dismiss="modal" aria-label="Close" ref={downloadClsRef} />
            <div className="modal-body text-center py-4">
              <div style={{
                width: 56, height: 56, borderRadius: '50%', backgroundColor: `${BRAND}18`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
              }}>
                <div className="spinner-border" role="status" style={{ color: BRAND, width: 28, height: 28, borderWidth: 3 }}>
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
              <p style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>Generating file…</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-start">

        {/* Left: action buttons + CSV */}
        <div className="col-sm-4 col-md-3 mb-3">
          {(isAdmin || role === 'partner') && (
            <Link to="create" style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.5rem 1.25rem', borderRadius: 9999, marginBottom: '0.75rem',
              background: `linear-gradient(135deg, ${BRAND_DARK}, ${BRAND})`,
              color: '#fff', fontWeight: 700, fontSize: '0.875rem', textDecoration: 'none',
              boxShadow: `0 4px 14px ${BRAND}55`, width: '100%', justifyContent: 'center',
            }}>
              <FontAwesomeIcon icon={isAdmin ? faLock : faHandshake} />
              <span>{isAdmin ? 'Add Packages' : t('packages.create_packages')}</span>
            </Link>
          )}
          <div className="input-group">
            <button
              className="btn btn-sm btn-primary"
              type="button"
              onClick={getPackagesCsv}
            >
              <FontAwesomeIcon icon={faFileCsv} className="me-1" />
              <span className="fw-bold">CSV</span>
            </button>
            <select
              className="form-select form-select-sm"
              defaultValue="10"
              onChange={e => setCsv(e.target.value)}
            >
              {['10', '50', '100', '200', '500', '1000'].map(v => (
                <option key={v} value={v}>{v === '1000' ? 'All' : v}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Right: search panel */}
        <div className="col-sm-8 col-md-9 mb-3">
          <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <button
              type="button"
              onClick={() => setSearchOpen(p => !p)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.75rem 1.25rem', background: searchOpen ? '#f8fafc' : '#fff',
                border: 'none', borderBottom: searchOpen ? '1px solid #e2e8f0' : 'none',
                cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: '#475569', textAlign: 'left',
              }}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <span>{t('utilities.search')}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {searchOpen && (
              <div style={{ padding: '1.25rem' }}>
                <Formik initialValues={filters} onSubmit={handleSearch} enableReinitialize>
                  <Form>
                    {isAdmin && (
                      <div className="row align-items-center mb-3">
                        <label className="col-sm-2 col-form-label col-form-label-sm">Partner</label>
                        <div className="col-sm-10">
                          <Field as="select" className="form-select form-select-sm" name="partner">
                            <option value="0">Seleccione un socio</option>
                            {allPartner.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}
                          </Field>
                        </div>
                      </div>
                    )}
                    <div className="row align-items-center mb-3">
                      <label className="col-sm-2 col-form-label col-form-label-sm">{t('packages.packages')}</label>
                      <div className="col-sm-10">
                        <Field type="text" className="form-control form-control-sm" name="_package" placeholder={t('packages.packages')} />
                      </div>
                    </div>
                    <div className="row align-items-center mb-3">
                      <label className="col-sm-2 col-form-label col-form-label-sm">{t('packages.compatibility_products')}</label>
                      <div className="col-sm-10">
                        <Field type="text" className="form-control form-control-sm" name="products" placeholder={t('packages.compatibility_products')} />
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="row align-items-center mb-3">
                        <label className="col-sm-2 col-form-label col-form-label-sm">User Email</label>
                        <div className="col-sm-10">
                          <Field type="text" className="form-control form-control-sm" name="useremail" placeholder="User email" />
                        </div>
                      </div>
                    )}
                    <div className="row align-items-center mb-3">
                      <label className="col-sm-2 col-form-label col-form-label-sm">{t('packages.status')}</label>
                      <div className="col-sm-10">
                        <Field as="select" className="form-select form-select-sm" name="status">
                          <option value="0">{t('packages.select_an_option')}</option>
                          <option value="1">{t('status.available')}</option>
                          <option value="2">{t('status.disabled')}</option>
                          <option value="3">{t('status.activated')}</option>
                          <option value="4">{t('status.registered')}</option>
                          <option value="5">{t('status.expired')}</option>
                          <option value="6">{t('status.renovated')}</option>
                        </Field>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="row align-items-center mb-3">
                        <label className="col-sm-2 col-form-label col-form-label-sm">User type</label>
                        <div className="col-sm-10">
                          <Field as="select" className="form-select form-select-sm" name="usertype">
                            <option value="">Select an option</option>
                            <option value="admin">Admin</option>
                            <option value="partner">Partner</option>
                            <option value="customer">Customer</option>
                          </Field>
                        </div>
                      </div>
                    )}
                    <button className="btn btn-sm btn-bd-primary" type="submit">
                      <span className="fw-bold">{t('utilities.search_btn')}</span>
                    </button>
                  </Form>
                </Formik>
              </div>
            )}
          </div>
        </div>

        {/* DataTable */}
        <div className="col-md-12">
          <div className="card card-shadow">
            <DataTable
              columns={columns}
              data={data}
              dense
              fixedHeader
              fixedHeaderScrollHeight="550px"
              progressPending={loading}
              pagination
              paginationPerPage={perPage}
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationComponentOptions={paginationComponentOptions}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              progressComponent={<CustomLoader />}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Packages
